import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import { ListFilter } from 'lucide-react';
import { useMemo } from 'react';
import type { DropdownItemProps } from 'semantic-ui-react';
import type { StringKeyOf } from 'type-fest';

import type { Filter } from '@models';
import { SearchDropdown } from '@components/search-dropdown';
import ToolbarButton from '@components/toolbar-button';

import s from './filter-picker.scss';

export interface FilterPickerProps<State> {
  className?: string;
  disabled?: boolean;
  onSelect: (filter: Filter<StringKeyOf<State>>) => void;
  options: Filter<StringKeyOf<State>>[];
  selectedKeys: ReadonlyArray<StringKeyOf<State>>;
}

/**
 * A dropdown button for adding filters to a list view. Pairs with `FilterBar`,
 * which renders the active filter pills. The two were previously coupled in a
 * single component — they are now decoupled so a view can place the picker
 * (typically right-aligned with other toolbar actions) independently of the
 * active-filters row (which wraps below).
 *
 * The menu stays open across selections so users can add several filters in
 * a row; it closes on outside click or Escape.
 *
 * Options already present in `selectedKeys` and filters marked `hidden` are
 * excluded from the menu.
 */
export const FilterPicker = <State,>({
  className,
  disabled,
  onSelect,
  options,
  selectedKeys,
}: FilterPickerProps<State>) => {
  useStyles(s);

  const dropdownOptions = useMemo<DropdownItemProps[]>(
    () =>
      options
        .filter(({ hidden, key }) => !hidden && !selectedKeys.includes(key))
        .map(({ key, name }) => ({
          content: name || key,
          key,
          text: name || key,
          value: key,
        })),
    [options, selectedKeys],
  );

  return (
    <SearchDropdown
      aria-label="Filter"
      className={classNames(s.root, className)}
      closeOnSelect={false}
      direction="left"
      disabled={disabled}
      emptyMessage="No filters available"
      menuClassName={s.menu}
      onSelect={option => {
        const selected = options.find(filter => filter.key === option.value);
        if (selected) onSelect(selected);
      }}
      options={dropdownOptions}
      trigger={
        <ToolbarButton
          content="Filter"
          disabled={disabled}
          icon={<ListFilter />}
        />
      }
      floating
    />
  );
};

export default FilterPicker;
